
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarInset
} from '@/components/ui/sidebar';
import { toast } from '@/components/ui/sonner';
import { Bell, Heart, History, LayoutDashboard, Settings } from 'lucide-react';
import StatsTab from '@/components/dashboard/StatsTab';
import FavoritesTab from '@/components/dashboard/FavoritesTab';
import HistoryTab from '@/components/dashboard/HistoryTab';
import NotificationsTab from '@/components/dashboard/NotificationsTab';
import SettingsTab from '@/components/dashboard/SettingsTab';
import useAuth from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';
import NavBar from '@/components/NavBar';

const Dashboard = () => {
  const { user, profile, loading, session, authChangeProcessed } = useAuth();
  const [activeTab, setActiveTab] = useState('stats');

  // Show loading state while authentication is being processed
  if (loading || !authChangeProcessed) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-6 h-6 border-4 border-b-transparent border-peach-orange rounded-full"></div>
      </div>
    );
  }

  // Only redirect if auth has been processed and user is not authenticated
  if (authChangeProcessed && !user) {
    return <Navigate to="/login" />;
  }
  
  // Map for tab navigation
  const tabs = [
    { id: 'stats', label: 'Overview', icon: <LayoutDashboard className="h-4 w-4" /> },
    { id: 'favorites', label: 'Favorites', icon: <Heart className="h-4 w-4" /> },
    { id: 'history', label: 'History', icon: <History className="h-4 w-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="h-4 w-4" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="h-4 w-4" /> }
  ];

  return (
    <div className="min-h-screen bg-warm-cream">
      <NavBar />
      
      <SidebarProvider defaultOpen={true}>
        <div className="flex">
          <Sidebar className="bg-white border-r">
            <SidebarHeader>
              <div className="p-4">
                <h2 className="text-lg font-semibold">{profile?.name || 'User'}'s Dashboard</h2>
                <p className="text-sm text-muted-foreground mt-1">Manage your sunny experience</p>
              </div>
            </SidebarHeader>
            <SidebarContent>
              <SidebarMenu>
                {tabs.map(tab => (
                  <SidebarMenuItem key={tab.id}>
                    <SidebarMenuButton
                      isActive={activeTab === tab.id}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.icon}
                      <span>{tab.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarContent>
          </Sidebar>
          
          <SidebarInset>
            <main className="container py-8">
              <Tabs value={activeTab} className="space-y-6">
                <TabsContent value="stats" className="m-0">
                  <StatsTab />
                </TabsContent>
                <TabsContent value="favorites" className="m-0">
                  <FavoritesTab />
                </TabsContent>
                <TabsContent value="history" className="m-0">
                  <HistoryTab />
                </TabsContent>
                <TabsContent value="notifications" className="m-0">
                  <NotificationsTab />
                </TabsContent>
                <TabsContent value="settings" className="m-0">
                  <SettingsTab />
                </TabsContent>
              </Tabs>
            </main>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default Dashboard;
